import { Upload } from "antd";
import { type UploadChangeParam } from "antd/lib/upload";
import { type UploadFile } from "antd/lib/upload/interface";
import { type IExternalServiceInfo } from "legacyComponents/FileUploadContainer.types";
import { type ReactNode, useCallback, useEffect, useRef } from "react";

import { cn } from "@btnet/utilities-core";

import { useWebviewContext } from "helpers/webview/WebviewContext";

import { MediaType, type TempFileTypes } from "types/enum";

import { useTracking } from "utilities/analytics/analytics";
import {
    beforeFileUpload,
    fileTypeBlacklist,
    type IUploadConfig,
    uploadAction,
} from "utilities/document/fileUpload.utils";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import { BTIconInboxOutlined, BTIconUploadOutlined } from "commonComponents/btWrappers/BTIcon";
import "commonComponents/btWrappers/BTUploadRaw/BTUploadRaw.less";
import { FileDragTarget } from "commonComponents/entity/media/FileDragTarget/FileDragTarget";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

export interface IBTUploadRawProps {
    children?: ReactNode;
    id: string;
    "data-testid": string;

    readOnly?: boolean;
    disabled?: boolean;
    /**
     * Controls the OS file picker's multi-select only (the `<input multiple>` attribute)
     */
    multiple?: boolean;
    /**
     * Maximum number of files allowed in the list
     */
    maxCount?: number;
    isDragger?: boolean;
    isArea?: boolean;
    jobId?: number;

    /**
     * @example ["*"] // all files
     * @example ["jpg", "gif", "doc", "docx"]
     */
    allowedFileTypes: string[];
    /**
     * If a file matches the both the blacklist and whitelist props,
     * the file is NOT allowed. Blacklists take precedence over whitelists.
     * @example ["jpg", "gif", "doc", "docx"]
     * @default our standard blacklist
     */
    disallowedFileTypes?: string[];

    tempFileType?: TempFileTypes;
    mediaType?: MediaType;

    value: UploadFile[] | undefined;

    uploadConfig?: IUploadConfig;

    showUploadList?: boolean;
    openFileDialogOnClick?: boolean;

    /**
     * When true, the inner DOM chain (drag wrapper → FileDragTarget → antd Upload internals)
     * is forced to flex so the dropzone children stretch to fill the parent's available
     * width and height. Use this when rendering an empty-state-style dropzone that should
     * fill its container. Default behavior (no flex chain) wraps content naturally.
     * @default false
     */
    fillContainer?: boolean;

    /*
     * Upload file properties that are required for external tempFile uploading
     */
    isExternal?: boolean;
    externalServiceInfo?: IExternalServiceInfo;

    beforeUpload?: (file: File, fileList: File[]) => Promise<boolean>;

    onChange: (info: UploadChangeParam) => void;
    onRemove: (file: UploadFile) => void;
}

const defaultBeforeUpload = async () => {
    return true;
};
const defaultUploadConfig = { shouldUploadFullResolutionPhoto: undefined };

export function BTUploadRaw({
    children,
    id,
    disabled,
    allowedFileTypes,
    value,
    readOnly,
    "data-testid": testid,
    tempFileType,
    multiple,
    maxCount,
    isDragger,
    isArea,
    mediaType,
    jobId,
    isExternal,
    externalServiceInfo,
    onChange,
    onRemove,
    disallowedFileTypes = fileTypeBlacklist,
    beforeUpload = defaultBeforeUpload,
    uploadConfig = defaultUploadConfig,
    showUploadList = true,
    openFileDialogOnClick = true,
    fillContainer = false,
}: IBTUploadRawProps) {
    const webview = useWebviewContext();

    // In a webview, drag-and-drop is not available so the accept
    // attribute is safe to set — it pre-filters the OS file picker without risking the
    // silent-drop issue on desktop. HEIC/HEIF are appended for photo uploads because they are
    // converted to JPEG before upload and may not appear in backend-provided allowedFileExtensions.
    const accept =
        webview && !(allowedFileTypes.length === 1 && allowedFileTypes[0] === "*")
            ? [
                  ...new Set([
                      ...allowedFileTypes.map((ext) => `.${ext}`),
                      ...(mediaType === MediaType.Photo ? [".heic", ".heif"] : []),
                  ]),
              ].join(",")
            : undefined;

    const { Track } = useTracking({
        component: "Upload Raw",
        uniqueId: testid,
    });

    const inputRef = useRef<Element | null>(null);
    const removeDirectoryPropFromInput = useCallback(() => {
        if (!inputRef.current?.isConnected) {
            const input = document.querySelector(`input#${id}`);
            inputRef.current = input;
        }

        // browsers do not support selecting both individual files AND directories in the file picker https://stackoverflow.com/questions/42633306/how-to-allow-the-user-to-pick-any-file-or-directory-in-an-input-type-file-ta
        // rc-upload couples webkitdirectory with the ability to drag and drop directories https://github.com/react-component/upload/blob/master/src/AjaxUploader.tsx
        // to get the best user experience, we want the ability to drag and drop directories, but only browse for files, so we remove the neccisary attributes
        // this can be removed if browsers support picking files and folders, or if rc-upload decouples browsing from drag and drop
        if (inputRef.current?.hasAttribute("webkitdirectory")) {
            inputRef.current.removeAttribute("webkitdirectory");
        }
        if (inputRef.current?.hasAttribute("directory")) {
            inputRef.current.removeAttribute("directory");
        }
    }, [id]);

    useEffect(() => {
        // ant will readd this prop on every render so we need to remove it
        removeDirectoryPropFromInput();
    });

    const handleChange = useCallback(
        (info: UploadChangeParam) => {
            // When customRequest's response carries post-conversion metadata
            // (e.g. the mobile HEIC bridge swaps .heic → .jpg server-side and
            // returns the JPEG's mime/size), surface those values on info.file
            // so antd's list shows the converted file. antd otherwise keeps
            // these fields locked to the originally-picked file's values forever.
            if (info.file.status === "done" && info.file.response) {
                const response = info.file.response as {
                    fileName?: string;
                    mimeType?: string;
                    size?: number;
                    thumbnail?: string;
                };
                if (response.fileName && response.fileName !== info.file.name) {
                    info.file.name = response.fileName;
                }
                if (response.mimeType && response.mimeType !== info.file.type) {
                    info.file.type = response.mimeType;
                }
                if (typeof response.size === "number" && response.size !== info.file.size) {
                    info.file.size = response.size;
                }
                if (response.thumbnail && response.thumbnail !== info.file.thumbUrl) {
                    info.file.thumbUrl = response.thumbnail;
                }
            }
            onChange(info);
        },
        [onChange],
    );

    // Should introduce accept and onDrop props in future. We now have an function for onDrop in fileUpload.utils
    // but error toast doesn't display when dropping an incorrect file type using isArea display below. onDrop is
    // only triggered when using Upload.Dragger and the accept prop prevents beforeUpload from running when file invalid.
    const uploadProps = {
        id,
        "data-testid": testid,
        accept,
        customRequest: tempFileType
            ? uploadAction(tempFileType, uploadConfig, {
                  mediaType,
                  jobId,
                  isExternal,
                  externalServiceInfo,
              })
            : undefined,
        multiple,
        maxCount,
        defaultFileList: value,
        fileList: value,
        onChange: handleChange,
        onRemove,
        beforeUpload: beforeFileUpload(
            allowedFileTypes,
            beforeUpload,
            false,
            disallowedFileTypes,
            mediaType,
        ),
        disabled: readOnly || disabled,
        showUploadList,
        openFileDialogOnClick,
        directory: true,
    };

    if (readOnly) {
        return (
            <Track>
                <ValueDisplay
                    value={
                        !value || value.length === 0 ? undefined : (
                            // eslint-disable-next-line react/forbid-elements
                            <Upload {...uploadProps} />
                        )
                    }
                />
            </Track>
        );
    }

    if (isDragger) {
        return (
            <Track>
                <div className="dropbox">
                    {/* eslint-disable-next-line react/forbid-elements */}
                    <Upload.Dragger {...uploadProps}>
                        <p className="ant-upload-drag-icon">
                            <BTIconInboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                            <BTExternalLink isUnderline={true}>Browse</BTExternalLink> or drag{" "}
                            {multiple ? "file(s)" : "a file"} here to upload
                        </p>
                        {children}
                    </Upload.Dragger>
                </div>
            </Track>
        );
    }

    if (isArea) {
        return (
            <Track>
                <div
                    className={cn({
                        "BTUploadRaw-FillContainer": fillContainer,
                    })}
                    onMouseDown={removeDirectoryPropFromInput}
                >
                    <FileDragTarget
                        allowDrop={!uploadProps.disabled}
                        forceDragOverDropEffect
                        render={() => (
                            // eslint-disable-next-line react/forbid-elements
                            <Upload
                                {...uploadProps}
                                className={cn("BTUploadRaw-UploadArea", {
                                    "BTUploadRaw-Unclickable": !openFileDialogOnClick,
                                })}
                            >
                                {children}
                            </Upload>
                        )}
                    />
                </div>
            </Track>
        );
    }

    const buttonActionText = value === undefined || multiple ? "Choose" : "Replace";
    const buttonFilesText = multiple ? "File(s)" : "File";

    return (
        <Track>
            {/* eslint-disable-next-line react/forbid-elements */}
            <Upload {...uploadProps}>
                <BTButtonDeprecated
                    disabled={disabled}
                    data-testid="upload"
                    icon={<BTIconUploadOutlined />}
                    onMouseDown={removeDirectoryPropFromInput}
                >
                    {buttonActionText} {buttonFilesText}
                </BTButtonDeprecated>
            </Upload>
        </Track>
    );
}
